// Settings → Personal Access Tokens panel. Lists the caller's OWN
// PATs, mints new ones with a tier picker (0.1.39+), and revokes.
//
// PATs are self-scoped: `pats:manage` is granted to every role by
// default because there's no way for one user to see or affect
// another user's tokens through /api/v1/pats — the routes hard-filter
// by session userId server-side.
//
// The mint form UX matches the shipped API contract:
//   - Name is required.
//   - Tier is required. Named tiers ('read-only', 'update', 'create',
//     'admin') auto-heal when the tier bundle gains caps in future
//     Tasko releases; 'custom' is a frozen scope snapshot.
//   - 'scopes[]' is only accepted (and required) when tier === 'custom'.
//     Named tier + non-empty scopes[] is a HARD 422 server-side — the
//     UI enforces the same rule so operators see the constraint before
//     they hit submit.
//   - The raw token is shown ONCE in a reveal banner and never re-fetched.
//
// See Captain Tasko `docs/pats/api.md` for the full contract.
//
// CTSK-204 (2026-07-29): brought this panel's row/header vocabulary into
// line with `apps/web/src/pages/ExternalClientsNest.tsx` — header
// explainer card, tab strip with counts, State + Kind columns,
// relative-time last-used, row-clickable (surface reserved; no detail
// page yet), and the External-Clients-style outlined action buttons —
// while preserving CTSK-192's MCP-ROLE badge, PAT id column, and Open
// request action. See the CTSK-204 ship comment for judgment calls made
// where the ticket's description didn't quite match the reference file
// (action-button color; Kind iconography) or the live API (GET /pats
// drops revoked tokens server-side — resolved by CTSK-319: the panel
// now requests ?includeRevoked=true so the Revoked tab is populated).
//
// CTSK-204 follow-up (2026-07-29, same day): the initial ship above kept
// the 9-column CTSK-192 layout with a `tableLayout: 'fixed'` colgroup +
// a State column that rendered as badge + expiry-date on two lines. In
// practice that forced every row to double-height and made the whole
// table read as visually "stacked" — the MCP-ROLE chip in particular
// looked like it was floating on its own line because the row was tall
// enough for the eye to read each cell as vertically separated. Fix
// consolidates to 6 columns (Name | Type | PAT id | Last used | State |
// Actions), drops the fixed layout, merges Tier + Kind into a single
// "Type" cell rendered inline, and moves secondary lines (org lock,
// expiry date, created-at) into title tooltips. Result: single-line
// rows, natural column sizing, same information density preserved via
// tooltips + the coming detail page. Now matches ExternalClientsNest's
// 5-column grammar.
//
// CTSK-313 (2026-08-10): the custom-tier scope picker's flat ~70-cap
// checkbox grid is now grouped into collapsible sections by resource
// domain (documents/workitems/pats/tags/tools/nests/comments/projects/
// workflows + an "other" fallback), with a client-side substring search
// box above the list. Sections default collapsed unless they contain a
// selected cap; all matching sections auto-expand while a filter is
// active. Selection/mint semantics unchanged — presentation only.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import {
  CAPABILITIES,
  PAT_TIER_NAMES,
  PAT_TIER_DESCRIPTIONS,
  ROLE_CATALOG,
  type PatTierName,
  type PersonalAccessToken,
  type RoleDefinition,
} from '@captain-tasko/shared';
import { apiFetch, apiList, apiSingle, ApiError } from '../lib/api.js';
import { copyToClipboard } from '../lib/clipboard.js';
import { toast } from './Toast.js';
import { CollapsiblePanel } from './CollapsiblePanel.js';
import { StateBadge, patState, type PatState } from './StateBadge.js';
import { KindIcon, type PatKind } from './KindIcon.js';
import {
  RowTable,
  useUrlTableSort,
  rowActionButtonStyle,
  type RowTableColumn,
} from './shared-table/RowTable.js';
import {
  ToolbarRow,
  ChipFilterButton,
  readUrlParam,
  writeUrlParam,
  type ToolbarOption,
} from './shared-table/ToolbarRow.js';
import type { Accessors } from '../lib/sort.js';
import {
  envFilenameFor,
  buildSettingsSessionEnv,
  triggerDownload,
  pickApiBaseUrl,
} from '../lib/env-file.js';
import { Copy, Download, KeyRound, Trash2, ExternalLink } from 'lucide-react';

interface MintResponse extends PersonalAccessToken {
  token: string;
}

interface MintEnvelope {
  data: MintResponse;
  warnings?: { droppedNoCap?: string[] };
}

// 0.1.54 — org picker types. Kept local to the panel; if OrgSwitcher's
// OrgMembership shape drifts, this stays stable against the /orgs
// contract we care about (id + name + slug).
interface OrgOption {
  id: string;
  slug: string;
  name: string;
}

interface WhoAmI {
  activeOrgId: string | null;
}

/** Human-friendly tier ordering — read-only → admin, custom last. */
const TIER_ORDER: PatTierName[] = ['read-only', 'update', 'create', 'admin', 'custom'];

/** CTSK-250 — one-line dropdown label for a catalog role:
 *  `<handle> — <first sentence of description> · <N caps>`.
 *  The catalog descriptions are multi-sentence audit prose; the first
 *  sentence is the operator-scan line the dropdown wants. The 'custom'
 *  catalog entry renders as the explicit no-template escape hatch
 *  (last in catalog order, per the ROLE_CATALOG seed comment). */
function roleOptionLabel(r: RoleDefinition): string {
  if (r.handle === 'custom') return 'custom (no template)';
  const stop = r.description.indexOf('. ');
  const oneLine = stop === -1 ? r.description : r.description.slice(0, stop + 1);
  return `${r.handle} — ${oneLine} · ${r.capabilities.length} caps`;
}

/** Compact color per tier for the badge — pulled from CSS vars so
 *  dark/light modes work uniformly. */
function tierBadgeColor(tier: PatTierName): string {
  switch (tier) {
    case 'read-only': return 'var(--text-muted)';
    case 'update':    return 'var(--accent-primary)';
    case 'create':    return 'var(--warning-primary)';
    case 'admin':     return 'var(--danger-primary, #B3261E)';
    case 'custom':    return 'var(--text-secondary)';
  }
}

// ── CTSK-204 — Kind + State + tab-strip helpers ──────────────────────
// Bring this panel's row-level vocabulary into line with
// ExternalClientsNest.tsx (the "good" example this ticket targets).

/**
 * Heuristic split between "Claude MCP" and "External MCP" for MCP-ROLE
 * PATs (accessRequestDocId set). The PAT record only carries the
 * mint-time sourceTag, not the originating access-request's clientKind
 * (that lives on the linked document, not denormalized onto the PAT —
 * see the accessRequestDocId field comment in packages/shared/src/types/pat.ts).
 * Threading clientKind onto the PAT row would be a schema change, out of
 * scope for this presentation-only ticket, so this matches on sourceTag
 * naming conventions observed across this codebase's own Cowork/Claude
 * sessions (e.g. `cpt-tasko-main`, and the `cse_…` / `local_…` canonical
 * session-key shapes tasko-mcp derives tails from). Good enough for the
 * common case; a mis-detected row still renders correctly as "External
 * MCP" (safe default), just mislabeled. Flagged as a follow-up candidate
 * in the ship comment: thread clientKind through to the PAT row so this
 * can be an exact match instead of a heuristic.
 */
function looksLikeClaudeSession(sourceTag: string | null | undefined): boolean {
  if (!sourceTag) return false;
  const tag = sourceTag.toLowerCase();
  return (
    tag.startsWith('cpt-') ||
    tag.startsWith('sgt-') ||
    tag.startsWith('cowork-') ||
    tag.startsWith('claude-') ||
    tag.startsWith('cse_') ||
    tag.startsWith('local_')
  );
}

function deriveKind(p: { accessRequestDocId?: string | null; sourceTag?: string | null }): PatKind {
  if (!p.accessRequestDocId) return 'hand-mint';
  return looksLikeClaudeSession(p.sourceTag) ? 'claude-mcp' : 'external-mcp';
}

/** "10h ago" / "3d ago" / calendar date beyond a week. Mirrors
 *  ExternalClientsNest.tsx's inline formatRelative. Kept as a local copy
 *  rather than extracted to a shared lib, to keep this ticket's touched
 *  files to PatsPanel + its two new companion components (StateBadge,
 *  KindIcon) — matches the ticket's "single primary file" framing. A
 *  future ticket could dedupe both call sites into
 *  apps/web/src/lib/format.ts. */
function formatRelativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const now = Date.now();
  const diff = now - then;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

type PatFilterTab = 'active' | 'mcp-role' | 'revoked' | 'expired' | 'all';

const TAB_ORDER: PatFilterTab[] = ['active', 'mcp-role', 'revoked', 'expired', 'all'];
const TAB_LABELS: Record<PatFilterTab, string> = {
  active: 'Active',
  'mcp-role': 'MCP-Role',
  revoked: 'Revoked',
  expired: 'Expired',
  all: 'All',
};
const TAB_COLORS: Record<PatFilterTab, string> = {
  active: '#22C55E',
  'mcp-role': '#7c3aed',
  revoked: '#EF4444',
  expired: '#6B7280',
  all: 'var(--accent-primary)',
};

/** Whether a PAT row belongs in a given tab. Tabs are NOT mutually
 *  exclusive — Kind (mcp-role) and State (active/expiring/expired/
 *  revoked) are independent axes, same as the ticket's own tab list
 *  ("Active · MCP-Role · Revoked · Expired · All") mixes both rather
 *  than picking one dimension. */
function matchesTab(state: PatState, isMcpRole: boolean, tab: PatFilterTab): boolean {
  switch (tab) {
    case 'active': return state === 'active' || state === 'expiring_soon';
    case 'mcp-role': return isMcpRole;
    case 'revoked': return state === 'revoked';
    case 'expired': return state === 'expired';
    case 'all': return true;
  }
}

/** CTSK-456 — quarterly custom-tier PAT review runbook
 *  (docs/runbooks/custom-pat-review.md, CTSK-431 §6 Decision 4).
 *  `reviewDueAt` is only populated for `tier === 'custom'` rows
 *  (migration 0059 backfill + non-goals: non-custom tiers untouched),
 *  but the tier check is explicit here too so a future backend change
 *  can't silently start flagging named-tier PATs. Orthogonal to
 *  `PatState` — a token can be "active" (not expired/revoked) and
 *  simultaneously review-overdue. */
function isCustomPatReviewOverdue(p: { tier: PatTierName; reviewDueAt?: string | null }): boolean {
  if (p.tier !== 'custom' || !p.reviewDueAt) return false;
  const dueMs = new Date(p.reviewDueAt).getTime();
  return !Number.isNaN(dueMs) && dueMs < Date.now();
}

// ── CTSK-313 — custom-scopes grouping + search ───────────────────────
// The custom-tier scope picker used to render ALL_CAPABILITIES-scale
// (~70 caps) as one flat checkbox grid — operators hunted visually.
// Group caps by their `<resource>:` prefix into collapsible sections
// (brief-specified domain order, everything else under "other"), with
// a client-side substring filter above the list.

/** Section order per the CTSK-313 brief. Any cap whose resource prefix
 *  isn't in this list buckets into OTHER_SCOPE_DOMAIN (fallback). */
const SCOPE_DOMAIN_ORDER = [
  'documents', 'workitems', 'pats', 'tags', 'tools',
  'nests', 'comments', 'projects', 'workflows',
] as const;
const OTHER_SCOPE_DOMAIN = 'other';

function capScopeDomain(cap: string): string {
  const idx = cap.indexOf(':');
  const prefix = idx === -1 ? cap : cap.slice(0, idx);
  return (SCOPE_DOMAIN_ORDER as readonly string[]).includes(prefix)
    ? prefix
    : OTHER_SCOPE_DOMAIN;
}

interface ScopeSection {
  domain: string;
  caps: string[];
}

/** Bucket caps into brief-ordered sections, optionally filtered by a
 *  case-insensitive substring match against the cap name. Sections with
 *  no (visible) caps are omitted; within a section caps sort
 *  alphabetically (ticket AC 2 — CAPABILITIES declaration order mixes
 *  eras of cap additions together). */
function groupCapabilities(caps: readonly string[], filter: string): ScopeSection[] {
  const q = filter.trim().toLowerCase();
  const visible = q ? caps.filter((c) => c.toLowerCase().includes(q)) : [...caps];
  const buckets = new Map<string, string[]>();
  for (const c of visible) {
    const d = capScopeDomain(c);
    const bucket = buckets.get(d);
    if (bucket) bucket.push(c);
    else buckets.set(d, [c]);
  }
  return [...SCOPE_DOMAIN_ORDER, OTHER_SCOPE_DOMAIN]
    .filter((d) => buckets.has(d))
    .map((d) => ({ domain: d, caps: buckets.get(d)!.sort((a, b) => a.localeCompare(b)) }));
}

/** Header label for a scope section — mirrors the brief's `<domain>:*`
 *  vocabulary; the fallback bucket reads as prose. */
function scopeSectionLabel(domain: string): string {
  return domain === OTHER_SCOPE_DOMAIN ? 'other / uncategorized' : `${domain}:*`;
}

// CTSK-466: the outlined pill-style action button shape moved to the
// shared table shell (shared-table/RowTable.tsx rowActionButtonStyle)
// so ExternalClientsNest picks up the identical treatment. Values are
// byte-identical to the previous local actionButtonBaseStyle.
const actionButtonBaseStyle = rowActionButtonStyle;

// ── CTSK-466 — sort + toolbar filters + archive ──────────────────────

/** Row shape the list renders — PAT + derived per-row meta. */
interface PatRowMeta {
  pat: PersonalAccessToken;
  isMcpRole: boolean;
  state: PatState;
  kind: PatKind;
}

/** Column config for the shared RowTable shell. Keys match
 *  PAT_SORT_ACCESSORS; Actions is unsortable. */
const PAT_COLUMNS: readonly RowTableColumn[] = [
  { key: 'name', label: 'Name' },
  { key: 'type', label: 'Type' },
  { key: 'id', label: 'PAT id' },
  { key: 'lastUsed', label: 'Last used' },
  { key: 'state', label: 'State' },
  { key: 'actions', label: 'Actions', sortable: false, align: 'right' },
];

/** Sort accessors (lib/sort.ts normalize handles nulls-last + ISO
 *  strings). Type sorts by the displayed label vocabulary: MCP-ROLE
 *  rows group together, others by tier name. */
const PAT_SORT_ACCESSORS: Accessors<PatRowMeta> = {
  name: (r) => r.pat.name,
  type: (r) => (r.isMcpRole ? 'mcp-role' : r.pat.tier),
  id: (r) => r.pat.id,
  lastUsed: (r) => r.pat.lastUsedAt ?? null,
  state: (r) => r.state,
};

const PAT_KIND_OPTIONS: readonly ToolbarOption[] = [
  { value: 'hand-mint', label: 'Hand-mint' },
  { value: 'claude-mcp', label: 'Claude MCP' },
  { value: 'external-mcp', label: 'External MCP' },
];

const PAT_STATE_OPTIONS: readonly ToolbarOption[] = [
  { value: 'active', label: 'Active' },
  { value: 'expiring_soon', label: 'Expiring soon' },
  { value: 'expired', label: 'Expired' },
  { value: 'revoked', label: 'Revoked' },
];

const ARCHIVE_AGE_MS = 30 * 24 * 60 * 60 * 1000;

/** Archived = revoked more than 30 days ago, or expired (any age —
 *  per the ticket's §5 spec: "Expired PATs (any age)"). Hidden by
 *  default on the All tab; the "Show archived" toggle restores them.
 *  The Expired/Revoked tabs are unaffected (tab filters run first and
 *  the archive filter only applies on All). */
function isArchivedPat(r: PatRowMeta, now = Date.now()): boolean {
  if (r.state === 'expired') return true;
  if (r.state === 'revoked' && r.pat.revokedAt) {
    const t = new Date(r.pat.revokedAt).getTime();
    return !Number.isNaN(t) && now - t > ARCHIVE_AGE_MS;
  }
  return false;
}

export function PatsPanel() {
  const qc = useQueryClient();

  // Mint form state.
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [tier, setTier] = useState<PatTierName>('update');
  const [customScopes, setCustomScopes] = useState<Set<string>>(new Set());
  const [expiresAt, setExpiresAt] = useState<string>(''); // YYYY-MM-DD or empty
  // 0.1.53 — session attribution tag. Stamped on the PAT at mint time
  // (CTSK-154 Phase B, 0.1.44) and auto-copied to every activity /
  // comment write made with this token. Optional; blank means writes
  // will show up with no session chip in the audit trail. The API-side
  // support has been there since 0.1.44; this input just exposes it in
  // the UI. See PatsPanel FR captured 2026-07-09 by Chadwiki.
  const [sourceTag, setSourceTag] = useState<string>('');
  // 0.1.54 — org lock (CTSK-169). Stamped on the PAT at mint time and
  // becomes the token's activeOrgId at every request. Prefilled to the
  // caller's current active org (whoami.activeOrgId); operator can pick
  // another org they're a member of via the dropdown. When only one
  // membership exists, the picker collapses to a static chip.
  const [orgId, setOrgId] = useState<string>('');
  // CTSK-250 — role-catalog dropdown. '' = nothing picked (placeholder);
  // 'custom' = the explicit "custom (no template)" entry (no seed, no
  // sourceRole stamped); any other value = a catalog handle whose
  // capabilities were seeded into customScopes (tier auto-flipped to
  // 'custom' and sourceRole stamped on the mint payload for the
  // "minted as role X" audit trail).
  const [sourceRole, setSourceRole] = useState<string>('');

  // CTSK-204 — active tab strip filter. Defaults to 'active', preserving
  // the panel's pre-existing "just show me the list" default behavior.
  const [activeTab, setActiveTab] = useState<PatFilterTab>('active');

  // CTSK-466 — All-tab toolbar filters + archive toggle, URL-mirrored
  // (?q=&kind=&state=&archived=1) via the same replaceState convention
  // as lib/use-list-search.ts. Settings' own ?section= param is
  // untouched — we only write our own keys.
  const [search, setSearchRaw] = useState(() => readUrlParam('q'));
  const [kindFilter, setKindFilterRaw] = useState(() => readUrlParam('kind'));
  const [stateFilter, setStateFilterRaw] = useState(() => readUrlParam('state'));
  const [showArchived, setShowArchivedRaw] = useState(() => readUrlParam('archived') === '1');
  const setSearch = (v: string) => {
    setSearchRaw(v);
    writeUrlParam('q', v.trim() ? v : null);
  };
  const setKindFilter = (v: string) => {
    setKindFilterRaw(v);
    writeUrlParam('kind', v || null);
  };
  const setStateFilter = (v: string) => {
    setStateFilterRaw(v);
    writeUrlParam('state', v || null);
  };
  const setShowArchived = (v: boolean) => {
    setShowArchivedRaw(v);
    writeUrlParam('archived', v ? '1' : null);
  };

  // CTSK-313 — custom-scopes picker UI state. `scopeSearch` is the
  // client-side substring filter; `scopeSectionsOpen` records explicit
  // operator open/collapse choices per domain (absent key = default:
  // collapsed unless the section has a selected cap; while a search is
  // active the default flips to open — see isScopeSectionOpen).
  const [scopeSearch, setScopeSearch] = useState('');
  const [scopeSectionsOpen, setScopeSectionsOpen] = useState<Record<string, boolean>>({});

  // Post-mint reveal banner.
  const [revealed, setRevealed] = useState<{
    name: string;
    tier: PatTierName;
    token: string;
    // CTSK-176-parity: `id` needed for the .env download's
    // TASKO_PAT_ID field. Was not previously tracked here since the
    // reveal banner only displayed name/tier/token.
    id: string;
    sourceTag: string | null;
    mintOrgId: string | null;
    // CTSK-250 — catalog handle the scopes were seeded from (audit chip).
    sourceRole: string | null;
    droppedNoCap: string[];
  } | null>(null);

  const patsQ = useQuery({
    queryKey: ['pats'],
    // CTSK-319 — includeRevoked so the Revoked tab has rows to show
    // (GET /pats drops revoked rows by default; the flag keeps the
    // self-scoped list contract, just without the revoked_at filter).
    queryFn: () => apiList<PersonalAccessToken>('/pats', { query: { includeRevoked: 'true' } }),
  });

  // 0.1.54 — org picker data. Shares cache with OrgSwitcher's
  // ['my-orgs', ...] key so the picker updates when the operator toggles
  // Show hidden orgs elsewhere. Both are read-only lists of what the
  // caller can pick from — including hidden orgs, since a PAT can
  // legitimately lock to a hidden internal org.
  const orgsQ = useQuery({
    queryKey: ['my-orgs', { includeHidden: true }],
    queryFn: () => apiList<OrgOption>('/orgs', { query: { includeHidden: 'true' } }),
  });
  const whoamiQ = useQuery({
    queryKey: ['whoami'],
    queryFn: () => apiSingle<WhoAmI>('/whoami'),
    staleTime: 60_000, // active org rarely changes mid-session
  });

  // CTSK-250 — named role catalog for the mint form's Role dropdown.
  // Fetched once (staleTime: Infinity — the server's copy is an
  // in-memory constant that only changes on deploy) and only while the
  // mint form is open. On error (e.g. a caller without roles:list) we
  // fall back to the shared ROLE_CATALOG import — same content the
  // server ships, one release older at worst (AC 2's static fallback).
  const roleCatalogQ = useQuery({
    queryKey: ['role-catalog'],
    queryFn: () => apiSingle<{ roles: RoleDefinition[] }>('/role-catalog'),
    enabled: createOpen,
    staleTime: Infinity,
    retry: false,
  });
  const catalogRoles: readonly RoleDefinition[] = roleCatalogQ.data?.roles ?? ROLE_CATALOG;
  // Deprecated handles stay resolvable for historical rows but shouldn't
  // be offered for new mints (RoleDefinition.deprecated contract).
  const pickableRoles = catalogRoles.filter((r) => !r.deprecated);
  // The seed annotation + mint payload only care about REAL role picks —
  // placeholder ('') and the explicit no-template entry ('custom') don't
  // stamp a sourceRole.
  const seedRole = sourceRole && sourceRole !== 'custom' ? sourceRole : null;
  // CTSK-373 follow-up (2026-08-21) — when the picked role carries
  // forcedSourceTag, the Session tag field stops being the operator's
  // free choice: it's locked to that value. Prevents re-mints of a
  // fixed shared identity (e.g. session-bootstrap) drifting to a
  // different hand-typed tag, which is exactly what broke
  // bootstrap-write-guard.ts's live sourceTag match on first smoke test.
  const forcedSourceTagRole = seedRole ? pickableRoles.find((r) => r.handle === seedRole) : null;
  const forcedSourceTag = forcedSourceTagRole?.forcedSourceTag ?? null;

  const orgOptions = orgsQ.data?.data ?? [];
  // apiSingle<T> unwraps the envelope's `.data` field, so whoamiQ.data
  // IS the WhoAmI payload directly (not `{data: WhoAmI}`). apiList by
  // contrast returns the full CollectionEnvelope, hence orgsQ.data.data.
  const activeOrgId = whoamiQ.data?.activeOrgId ?? null;

  // Prefill orgId once whoami + orgs settle. Only overwrites when the
  // picker hasn't been touched (empty string), so the user's explicit
  // choice survives a background refetch. Run in an effect (not the
  // render body) so React's strict-mode double-render doesn't burn a
  // spurious state write.
  useEffect(() => {
    if (createOpen && !orgId && activeOrgId && orgOptions.some((o) => o.id === activeOrgId)) {
      setOrgId(activeOrgId);
    }
  }, [createOpen, orgId, activeOrgId, orgOptions]);

  const mintMut = useMutation({
    mutationFn: () => {
      // Convert YYYY-MM-DD → ISO 8601 with midnight UTC; the schema
      // accepts either that or null/''.
      const iso = expiresAt ? `${expiresAt}T00:00:00Z` : undefined;
      const body: Record<string, unknown> = {
        name: name.trim(),
        tier,
      };
      if (tier === 'custom') body.scopes = [...customScopes];
      // CTSK-250 — preserve the catalog handle the scopes were seeded
      // from. Placeholder + the 'custom (no template)' sentinel mean
      // "no source role" — omit the field entirely on those paths.
      if (tier === 'custom' && seedRole) body.sourceRole = seedRole;
      if (iso) body.expiresAt = iso;
      const trimmedSourceTag = sourceTag.trim();
      if (trimmedSourceTag) body.sourceTag = trimmedSourceTag;
      // 0.1.54 — org lock. Explicit override always wins; if empty (no
      // picker interaction yet), let the server default from the cookie
      // session. Server-side validation confirms the caller is a member
      // of the chosen org before stamping mint_org_id.
      if (orgId) body.orgId = orgId;
      return apiFetch<MintEnvelope>('/pats', { method: 'POST', body });
    },
    onSuccess: (env) => {
      setRevealed({
        name: env.data.name,
        tier: env.data.tier,
        token: env.data.token,
        id: env.data.id,
        sourceTag: env.data.sourceTag ?? null,
        mintOrgId: env.data.mintOrgId ?? null,
        sourceRole: env.data.sourceRole ?? null,
        droppedNoCap: env.warnings?.droppedNoCap ?? [],
      });
      toast.success(`PAT minted: ${env.data.name}`);
      setCreateOpen(false);
      setName('');
      setTier('update');
      setCustomScopes(new Set());
      setExpiresAt('');
      setSourceTag('');
      setOrgId('');
      setSourceRole('');
      // CTSK-313 — scope-picker UI state resets with the rest of the form.
      setScopeSearch('');
      setScopeSectionsOpen({});
      qc.invalidateQueries({ queryKey: ['pats'] });
    },
    onError: (err: unknown) => toast.fromError(err, 'Failed to mint PAT'),
  });

  const revokeMut = useMutation({
    mutationFn: (id: string) => apiFetch(`/pats/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('PAT revoked');
      qc.invalidateQueries({ queryKey: ['pats'] });
    },
    onError: (err: unknown) => toast.fromError(err, 'Failed to revoke PAT'),
  });

  if (patsQ.isError) {
    const err = patsQ.error;
    const status = err instanceof ApiError ? err.status : 0;
    if (status === 403) {
      return (
        <div style={{ padding: 20, background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', color: 'var(--text-muted)', fontSize: 13 }}>
          Listing Personal Access Tokens requires the <code>pats:manage</code> capability.
        </div>
      );
    }
    return <div style={{ color: 'var(--danger-primary, #B3261E)' }}>Failed to load PATs.</div>;
  }

  const pats = patsQ.data?.data ?? [];
  // CTSK-319: the list now includes revoked rows (?includeRevoked=true),
  // so this filter does real work — the header count stays active-only.
  const activeCount = pats.filter((p) => !p.revokedAt).length;

  // CTSK-204 — derive per-row State + Kind once, then bucket into tabs.
  const rowsWithMeta = useMemo(
    () =>
      pats.map((p) => ({
        pat: p,
        isMcpRole: !!p.accessRequestDocId,
        state: patState({ revokedAt: p.revokedAt, expiresAt: p.expiresAt }),
        kind: deriveKind(p),
      })),
    [pats],
  );

  const tabCounts = useMemo(() => {
    const counts: Record<PatFilterTab, number> = {
      active: 0,
      'mcp-role': 0,
      revoked: 0,
      expired: 0,
      all: rowsWithMeta.length,
    };
    for (const r of rowsWithMeta) {
      for (const tab of TAB_ORDER) {
        if (tab !== 'all' && matchesTab(r.state, r.isMcpRole, tab)) counts[tab] += 1;
      }
    }
    return counts;
  }, [rowsWithMeta]);

  const filteredRows = useMemo(
    () => rowsWithMeta.filter((r) => matchesTab(r.state, r.isMcpRole, activeTab)),
    [rowsWithMeta, activeTab],
  );

  // CTSK-466 — All-tab pipeline: tab → archive → search → kind → state
  // → sort. Order follows use-list-search.ts's documented contract
  // (filters run before sort so the row counts stay honest); the
  // toolbar filters only exist on the All tab, other tabs pass
  // through untouched.
  const isAllTab = activeTab === 'all';
  const archivedCount = useMemo(
    () => (isAllTab ? filteredRows.filter((r) => isArchivedPat(r)).length : 0),
    [isAllTab, filteredRows],
  );
  const visibleRows = useMemo(() => {
    if (!isAllTab) return filteredRows;
    let rows = filteredRows;
    if (!showArchived) rows = rows.filter((r) => !isArchivedPat(r));
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter((r) =>
        [
          r.pat.name,
          r.pat.id,
          r.pat.prefix,
          r.pat.sourceTag ?? '',
          r.pat.sourceRole ?? '',
          r.pat.tier,
          r.kind,
        ]
          .join('\n')
          .toLowerCase()
          .includes(q),
      );
    }
    if (kindFilter) rows = rows.filter((r) => r.kind === kindFilter);
    if (stateFilter) rows = rows.filter((r) => r.state === stateFilter);
    return rows;
  }, [isAllTab, filteredRows, showArchived, search, kindFilter, stateFilter]);

  // Sort applies on every tab; state seeds from ?sort=&dir= and every
  // header click writes back (CTSK-466 AC 1).
  const { sorted: sortedRows, sort, onSortChange } = useUrlTableSort(
    visibleRows,
    PAT_SORT_ACCESSORS,
  );

  // CTSK-250 — role dropdown selection. Picking a real role flips the
  // tier to 'custom' and seeds the scope checkboxes from the role's
  // capabilities (operator can tweak or extend before minting). Picking
  // "custom (no template)" flips to the custom tier WITHOUT touching
  // scopes — no auto-seed, and deliberately no clear either (least
  // destructive to hand-picked checkboxes). Clearing back to the
  // placeholder changes nothing else.
  const onPickRole = (handle: string) => {
    setSourceRole(handle);
    if (!handle) {
      // Cleared back to placeholder — nothing else to reconcile; the
      // forced-tag effect below only fires while a real role is picked.
      return;
    }
    setTier('custom');
    if (handle === 'custom') return;
    const role = pickableRoles.find((r) => r.handle === handle);
    if (role) setCustomScopes(new Set(role.capabilities));
    // CTSK-373 follow-up — a forcedSourceTag role overwrites whatever
    // the operator had typed into Session tag. Deliberate: this field
    // isn't this role's free choice, so keeping a stale hand-typed
    // value visible (even disabled) would be misleading.
    if (role?.forcedSourceTag !== undefined) setSourceTag(role.forcedSourceTag);
  };

  // CTSK-313 — derived scope-picker views. `groupedScopes` is what
  // renders (search filter applied); the totals map keeps section
  // headers reporting whole-domain counts even when the filter is
  // hiding rows, so "(2/13)" always means "2 of this domain's 13 caps
  // selected" regardless of the current filter.
  const scopeSearchActive = scopeSearch.trim().length > 0;
  const groupedScopes = useMemo(() => groupCapabilities(CAPABILITIES, scopeSearch), [scopeSearch]);
  const scopeDomainTotals = useMemo(() => {
    const totals = new Map<string, number>();
    for (const c of CAPABILITIES) {
      const d = capScopeDomain(c);
      totals.set(d, (totals.get(d) ?? 0) + 1);
    }
    return totals;
  }, []);
  const scopeDomainSelectedCount = (domain: string): number => {
    let n = 0;
    for (const c of customScopes) if (capScopeDomain(c) === domain) n += 1;
    return n;
  };
  // Open resolution: an explicit operator toggle always wins; otherwise
  // default open while a search is active (auto-expand), or when the
  // section holds an active selection (so a returning operator lands on
  // their own picks), else collapsed.
  const isScopeSectionOpen = (domain: string): boolean =>
    scopeSectionsOpen[domain] ?? (scopeSearchActive || scopeDomainSelectedCount(domain) > 0);
  const onScopeSearchChange = (value: string) => {
    // Entering search mode drops explicit collapses so every matching
    // section auto-expands (brief rule) — including sections the
    // operator collapsed before typing.
    if (value.trim().length > 0 && !scopeSearchActive) setScopeSectionsOpen({});
    setScopeSearch(value);
  };

  const nameOk = name.trim().length > 0;
  const customOk = tier !== 'custom' || customScopes.size > 0;
  const canSubmit = nameOk && customOk && !mintMut.isPending;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* ── One-time token reveal ─────────────────────────────────── */}
      {revealed && (
        <section
          role="alert"
          style={{
            background: 'color-mix(in srgb, var(--warning-primary) 12%, var(--surface-raised))',
            border: '1px solid var(--warning-primary)',
            borderRadius: 'var(--radius-md)',
            padding: 16,
            display: 'flex', flexDirection: 'column', gap: 10,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
            <strong style={{ fontSize: 14, color: 'var(--warning-primary)' }}>
              <KeyRound size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />
              Token minted — copy it now
            </strong>
            <button
              type="button"
              onClick={() => setRevealed(null)}
              style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 12 }}
            >
              I've saved this
            </button>
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 500 }}>{revealed.name}</span>
            <span>·</span>
            <TierBadge tier={revealed.tier} />
            {revealed.sourceTag && (
              <>
                <span>·</span>
                <span
                  title={`Session tag stamped on every write from this token: ${revealed.sourceTag}`}
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 11,
                    background: 'var(--surface-sunken)',
                    color: 'var(--text-secondary)',
                    padding: '1px 6px',
                    borderRadius: 3,
                    border: '1px solid var(--border-subtle)',
                  }}
                >
                  {revealed.sourceTag}
                </span>
              </>
            )}
            {revealed.sourceRole && (
              // CTSK-250 — audit chip: which catalog role seeded this
              // token's scopes. Mirrors the sourceTag chip's styling.
              <>
                <span>·</span>
                <span
                  title={`Scopes seeded from role: ${revealed.sourceRole} (stamped on the token for the audit trail)`}
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 11,
                    background: 'var(--surface-sunken)',
                    color: 'var(--text-secondary)',
                    padding: '1px 6px',
                    borderRadius: 3,
                    border: '1px solid var(--border-subtle)',
                  }}
                >
                  role: {revealed.sourceRole}
                </span>
              </>
            )}
            {revealed.mintOrgId && (() => {
              // 0.1.54 — render the org lock as a name chip when we can
              // resolve the id to a friendly name, else as a mono ULID.
              // Falls back gracefully if orgs list hasn't loaded (rare
              // during the reveal banner's lifetime, but defensive).
              const org = orgOptions.find((o) => o.id === revealed.mintOrgId);
              const label = org ? org.name : revealed.mintOrgId;
              return (
                <>
                  <span>·</span>
                  <span
                    title={`Locked to org: ${label} (${revealed.mintOrgId}). Fail-closed if you lose membership.`}
                    style={{
                      fontSize: 11,
                      background: 'var(--surface-sunken)',
                      color: 'var(--text-secondary)',
                      padding: '1px 6px',
                      borderRadius: 3,
                      border: '1px solid var(--border-subtle)',
                    }}
                  >
                    org: {label}
                  </span>
                </>
              );
            })()}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <code
              style={{
                flex: 1,
                fontFamily: 'var(--font-mono)', fontSize: 13,
                padding: '8px 12px',
                background: 'var(--surface-raised)',
                border: '1px solid var(--border-strong)',
                borderRadius: 4,
                userSelect: 'all',
                wordBreak: 'break-all',
              }}
            >
              {revealed.token}
            </code>
            <button
              type="button"
              onClick={() => {
                // CTSK-282: copyToClipboard falls back to execCommand on
                // insecure origins (raw navigator.clipboard fails on http://).
                void copyToClipboard(revealed.token).then((ok) => {
                  if (ok) toast.success('Token copied');
                  else toast.error('Clipboard write failed');
                });
              }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '8px 12px', fontSize: 12,
                border: '1px solid var(--border-strong)', borderRadius: 4,
                background: 'var(--surface-raised)', cursor: 'pointer',
              }}
            >
              <Copy size={12} /> Copy
            </button>
            {/* CTSK-176 parity — Download the raw token as a ready-to-drop
               .env file. Requires a sourceTag (filename derives from it +
               the file is meant to live at ~/.config/tasko-mcp/<tag>.env).
               When sourceTag is absent the button is disabled with a
               title tip pointing at the field. CTSK-204: baseUrl now goes
               through the same pickApiBaseUrl() CTSK-211 fallback chain
               ExternalClientsNest.tsx uses, instead of the panel's old
               hardcoded ":4000" port swap. */}
            <button
              type="button"
              disabled={!revealed.sourceTag}
              onClick={() => {
                if (!revealed.sourceTag) return;
                const baseUrl = pickApiBaseUrl(null);
                const content = buildSettingsSessionEnv(
                  { name: revealed.name, token: revealed.token, id: revealed.id, sourceTag: revealed.sourceTag },
                  baseUrl,
                );
                triggerDownload(content, envFilenameFor(revealed.sourceTag));
                toast.success(`Downloaded ${envFilenameFor(revealed.sourceTag)}`);
              }}
              title={
                revealed.sourceTag
                  ? `Download as ~/.config/tasko-mcp/${envFilenameFor(revealed.sourceTag)}`
                  : 'Set a session tag when minting to enable .env download (the filename derives from the tag).'
              }
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '8px 12px', fontSize: 12,
                border: '1px solid var(--border-strong)', borderRadius: 4,
                background: revealed.sourceTag ? 'var(--surface-raised)' : 'var(--surface-sunken)',
                cursor: revealed.sourceTag ? 'pointer' : 'not-allowed',
                opacity: revealed.sourceTag ? 1 : 0.5,
              }}
            >
              <Download size={12} /> Download .env
            </button>
          </div>
          <p style={{ margin: 0, fontSize: 11, color: 'var(--text-muted)' }}>
            This is the only time the raw token will be shown. Save it in your daemon's
            <code style={{ margin: '0 4px' }}>.env</code> or secrets manager before dismissing.
            {revealed.sourceTag && (
              <>
                {' '}
                Use <strong>Download .env</strong> to save a ready-to-drop
                <code style={{ margin: '0 4px' }}>{envFilenameFor(revealed.sourceTag)}</code>
                for <code style={{ margin: '0 4px' }}>~/.config/tasko-mcp/</code>.
              </>
            )}
          </p>
          {revealed.droppedNoCap.length > 0 && (
            <div style={{ fontSize: 12, color: 'var(--warning-primary)' }}>
              <strong>Note:</strong> the following scopes were requested but you don't currently hold them,
              so they were dropped from the mint:{' '}
              <code>{revealed.droppedNoCap.join(', ')}</code>
            </div>
          )}
        </section>
      )}

      {/* ── Header + list ────────────────────────────────────────── */}
      <section style={{ background: 'var(--surface-raised)', padding: 20, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h2 style={{ margin: 0, fontSize: 16 }}>Personal Access Tokens</h2>
          {!createOpen && (
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '4px 10px', fontSize: 12,
                border: '1px solid var(--border-strong)', borderRadius: 6,
                background: 'var(--surface-raised)', color: 'var(--text-primary)',
                cursor: 'pointer',
              }}
            >
              <KeyRound size={12} /> New token
            </button>
          )}
        </div>

        {/* CTSK-204 delta 1 — header explainer card, analogous to
            ExternalClientsNest.tsx's "Onboarding a new client" card.
            Content per the ticket's proposal, plus a CTSK-194 note about
            the pats-operator role (called out explicitly in the ticket
            since that ticket added the role this UI should surface). */}
        <section
          style={{
            marginBottom: 16,
            padding: 14,
            background: 'color-mix(in srgb, var(--accent-primary) 6%, var(--surface-raised))',
            border: '1px solid color-mix(in srgb, var(--accent-primary) 30%, var(--border-subtle))',
            borderRadius: 'var(--radius-md)',
            fontSize: 13,
            lineHeight: 1.55,
          }}
        >
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <KeyRound size={18} style={{ marginTop: 2, color: 'var(--accent-primary)', flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, marginBottom: 4, color: 'var(--text-primary)' }}>
                About Personal Access Tokens
              </div>
              <div style={{ color: 'var(--text-secondary)' }}>
                Personal Access Tokens — bearer credentials for daemons + MCP servers. Every request is
                subject to live cap intersection. Tokens are self-scoped — only you can mint or revoke your
                own. See the PATs API reference for the effective-scope formula and the MCP-ROLE tier for
                the External Clients Nest mint path. The <code>pats-operator</code> role (CTSK-194) grants
                org-wide read visibility into access-request docs — mint one via the form above when someone
                needs that view without approve/deny/revoke.
              </div>
              <div style={{ marginTop: 8, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <a href="/help/pats-api" style={{ color: 'var(--accent-primary)', fontSize: 12, textDecoration: 'none' }}>
                  PATs API reference (§3.1 — effective-scope formula)
                </a>
                <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>·</span>
                <a href="/help/pats-api#section-8-6" style={{ color: 'var(--accent-primary)', fontSize: 12, textDecoration: 'none' }}>
                  §8.6 — admin endpoint
                </a>
                <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>·</span>
                <a href="/workitems/CTSK-192" style={{ color: 'var(--accent-primary)', fontSize: 12, textDecoration: 'none' }}>
                  CTSK-192 (MCP-ROLE)
                </a>
                <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>·</span>
                <a href="/workitems/CTSK-166" style={{ color: 'var(--accent-primary)', fontSize: 12, textDecoration: 'none' }}>
                  CTSK-166 Phase A (mint-from-approval)
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* ── Mint form ────────────────────────────────────────── */}
        {createOpen && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (canSubmit) mintMut.mutate();
            }}
            style={{
              padding: 16,
              background: 'var(--surface-sunken)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-md)',
              marginBottom: 12,
              display: 'flex', flexDirection: 'column', gap: 14,
            }}
          >
            {/* Name */}
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
              <span style={{ fontWeight: 500 }}>Name</span>
              <input
                type="text" value={name} onChange={(e) => setName(e.target.value)}
                placeholder="e.g. tasko-mcp on mini-me"
                required autoFocus maxLength={200}
                style={{ padding: '6px 10px', fontSize: 13, border: '1px solid var(--border-strong)', borderRadius: 4 }}
              />
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                Human label shown in this list. Not exposed to Bearer callers.
              </span>
            </label>

            {/* Org lock (0.1.54, CTSK-169). The PAT is stamped to a
                specific org at mint time and resolveBearerSession
                honors this on every request — no per-request re-derive
                from a membership sort. Prefilled to the caller's
                current active org (whoami.activeOrgId). Collapses to a
                static chip when the caller has only one membership. */}
            {orgOptions.length > 0 && (
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
                <span style={{ fontWeight: 500 }}>Organization</span>
                {orgOptions.length === 1 ? (
                  <div
                    style={{
                      padding: '6px 10px', fontSize: 13,
                      background: 'var(--surface-raised)',
                      border: '1px solid var(--border-subtle)',
                      borderRadius: 4, maxWidth: 360,
                      color: 'var(--text-secondary)',
                    }}
                  >
                    <strong style={{ color: 'var(--text-primary)' }}>{orgOptions[0]!.name}</strong>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, marginLeft: 8, color: 'var(--text-muted)' }}>
                      {orgOptions[0]!.slug}
                    </span>
                  </div>
                ) : (
                  <select
                    value={orgId}
                    onChange={(e) => setOrgId(e.target.value)}
                    style={{ padding: '6px 10px', fontSize: 13, border: '1px solid var(--border-strong)', borderRadius: 4, maxWidth: 360 }}
                  >
                    {orgOptions.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.name} ({o.slug})
                        {o.id === activeOrgId ? ' — active' : ''}
                      </option>
                    ))}
                  </select>
                )}
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  Locks this token to a specific organization. Prefilled to your
                  current active org. If your membership in this org is later
                  revoked, requests with this token return 401 (fail-closed).
                </span>
              </label>
            )}

            {/* Role dropdown (CTSK-250) — surfaces the named ROLE_CATALOG
                (CTSK-170/183) in the mint flow. Strictly additive: the tier
                picker below stays untouched; picking a role just drives it
                to 'custom' and seeds the scope checkboxes. Native <select>
                for free keyboard nav + screen-reader semantics (AC 9);
                aria-describedby points at the "seeded from role"
                annotation next to the Tier legend when a seed is live. */}
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
              <span style={{ fontWeight: 500 }}>Role (from catalog)</span>
              <select
                value={sourceRole}
                onChange={(e) => onPickRole(e.target.value)}
                aria-describedby={seedRole ? 'pat-role-seed-note' : undefined}
                style={{ padding: '6px 10px', fontSize: 13, border: '1px solid var(--border-strong)', borderRadius: 4, maxWidth: 560 }}
              >
                <option value="">— pick a role —</option>
                {pickableRoles.map((r) => (
                  <option key={r.handle} value={r.handle}>
                    {roleOptionLabel(r)}
                  </option>
                ))}
              </select>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                Picking a role flips the tier to <code>custom</code> and seeds the
                scope list from the role's capabilities — tweak below before minting
                if needed. The handle is stamped on the token for the audit trail.{' '}
                <a href="/help/role-catalog" style={{ color: 'var(--accent-primary)', textDecoration: 'none' }}>
                  Learn more about roles →
                </a>
              </span>
            </label>

            {/* Tier picker */}
            <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
              <legend style={{ fontSize: 13, fontWeight: 500, marginBottom: 6 }}>
                Tier
                {seedRole && (
                  // CTSK-250 — provenance annotation: makes it visible at
                  // the tier field WHY it's sitting on 'custom' with a
                  // pre-filled scope list. Referenced by the role select's
                  // aria-describedby.
                  <span
                    id="pat-role-seed-note"
                    style={{ marginLeft: 8, fontSize: 11, fontWeight: 400, color: 'var(--text-muted)' }}
                  >
                    seeded from role:{' '}
                    <code style={{ fontFamily: 'var(--font-mono)' }}>{seedRole}</code>
                  </span>
                )}
              </legend>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {TIER_ORDER.map((t) => (
                  <label
                    key={t}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: 8,
                      padding: 8,
                      borderRadius: 4,
                      background: tier === t ? 'var(--surface-raised)' : 'transparent',
                      border: `1px solid ${tier === t ? 'var(--accent-primary)' : 'var(--border-subtle)'}`,
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="radio" name="tier" value={t}
                      checked={tier === t}
                      onChange={() => {
                        setTier(t);
                        // CTSK-250 — manually leaving the custom tier
                        // invalidates the role seed (a named tier's
                        // expansion, not the role, decides the scopes now).
                        if (t !== 'custom') setSourceRole('');
                      }}
                      style={{ marginTop: 3 }}
                    />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <span style={{ fontSize: 13, fontWeight: 500 }}>
                        <TierBadge tier={t} />
                      </span>
                      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        {PAT_TIER_DESCRIPTIONS[t]}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
              <p style={{ margin: '8px 0 0 0', fontSize: 11, color: 'var(--text-muted)' }}>
                Named tiers <strong>auto-heal</strong> when the tier bundle gains capabilities in
                future Tasko releases — no re-mint needed. Custom tokens are frozen contracts.
              </p>
            </fieldset>

            {/* Custom scopes — only when tier === 'custom'.
                CTSK-313: the flat ~70-checkbox grid is now grouped into
                collapsible sections by resource domain, with a search box
                filtering caps client-side. Sections default collapsed
                unless they hold a selected cap; all matching sections
                auto-expand while a filter is active. Selection semantics
                (checkbox → customScopes Set → mint payload) unchanged. */}
            {tier === 'custom' && (
              <fieldset style={{ border: '1px dashed var(--border-strong)', borderRadius: 4, padding: 10, margin: 0 }}>
                <legend style={{ fontSize: 12, fontWeight: 500, padding: '0 6px' }}>
                  Custom scopes ({customScopes.size} selected)
                </legend>
                <input
                  type="search"
                  value={scopeSearch}
                  onChange={(e) => onScopeSearchChange(e.target.value)}
                  placeholder="Filter scopes…"
                  aria-label="Filter scopes"
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    padding: '5px 10px', fontSize: 12,
                    fontFamily: 'var(--font-mono)',
                    border: '1px solid var(--border-strong)', borderRadius: 4,
                    marginBottom: 8,
                  }}
                />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 300, overflowY: 'auto' }}>
                  {groupedScopes.length === 0 ? (
                    <div style={{ padding: 8, fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                      No scopes match "{scopeSearch.trim()}".
                    </div>
                  ) : (
                    groupedScopes.map(({ domain, caps }) => {
                      const open = isScopeSectionOpen(domain);
                      const selected = scopeDomainSelectedCount(domain);
                      const total = scopeDomainTotals.get(domain) ?? caps.length;
                      return (
                        <section key={domain} style={{ border: '1px solid var(--border-subtle)', borderRadius: 4 }}>
                          <button
                            type="button"
                            aria-expanded={open}
                            onClick={() => setScopeSectionsOpen((prev) => ({ ...prev, [domain]: !open }))}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 6,
                              width: '100%', boxSizing: 'border-box',
                              padding: '6px 8px', fontSize: 12,
                              border: 'none', borderRadius: 4,
                              background: 'var(--surface-raised)', color: 'var(--text-primary)',
                              cursor: 'pointer', textAlign: 'left',
                            }}
                          >
                            <span aria-hidden="true" style={{ fontSize: 10, color: 'var(--text-muted)', width: 10 }}>
                              {open ? '▾' : '▸'}
                            </span>
                            <code style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600 }}>
                              {scopeSectionLabel(domain)}
                            </code>
                            <span style={{ fontSize: 11, color: selected > 0 ? 'var(--accent-primary)' : 'var(--text-muted)' }}>
                              ({selected}/{total})
                            </span>
                          </button>
                          {open && (
                            <div
                              style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                                gap: 4, padding: 8,
                                borderTop: '1px solid var(--border-subtle)',
                              }}
                            >
                              {caps.map((c) => (
                                <label key={c} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                                  <input
                                    type="checkbox"
                                    checked={customScopes.has(c)}
                                    onChange={(e) => {
                                      const next = new Set(customScopes);
                                      if (e.target.checked) next.add(c);
                                      else next.delete(c);
                                      setCustomScopes(next);
                                    }}
                                  />
                                  <code style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{c}</code>
                                </label>
                              ))}
                            </div>
                          )}
                        </section>
                      );
                    })
                  )}
                </div>
                <p style={{ margin: '6px 0 0 0', fontSize: 11, color: 'var(--text-muted)' }}>
                  Scopes are intersected with your current caps at mint time. Any you don't
                  hold will be dropped and surfaced in the response.
                </p>
              </fieldset>
            )}

            {/* Session tag — 0.1.53 CTSK-168 companion. Stamps a
                session-attribution string on every activity + comment
                write made through this token. Renders as a muted
                mono-pill next to the actor's display name in the audit
                trail. See CTSK-154 Phase B for the underlying mechanism;
                this input is the UI catch-up.
                CTSK-373 follow-up (2026-08-21): when the picked role
                carries forcedSourceTag, this field is locked — it's no
                longer the operator's free choice, it's fixed to that
                role's identity. Prevents the exact drift (an operator
                hand-typing a different tag for session-bootstrap) that
                broke the server-side bootstrap-write-guard.ts match on
                first live smoke test. */}
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
              <span style={{ fontWeight: 500 }}>
                {forcedSourceTag ? 'Session tag (fixed by role)' : 'Session tag (optional)'}
              </span>
              <input
                type="text" value={sourceTag}
                onChange={(e) => setSourceTag(e.target.value)}
                readOnly={forcedSourceTag !== null}
                disabled={forcedSourceTag !== null}
                placeholder="e.g. cowork-coo-mini-me, bundle-install, cpt-tasko-main"
                maxLength={64}
                pattern="[a-zA-Z0-9_\-]*"
                title={forcedSourceTag ? `Fixed to "${forcedSourceTag}" for the "${seedRole}" role — pick a different role for a custom tag.` : undefined}
                style={{
                  padding: '6px 10px', fontSize: 13, fontFamily: 'var(--font-mono)',
                  border: '1px solid var(--border-strong)', borderRadius: 4, maxWidth: 360,
                  ...(forcedSourceTag ? { background: 'var(--surface-sunken)', cursor: 'not-allowed', opacity: 0.75 } : {}),
                }}
              />
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                {forcedSourceTag
                  ? `Fixed to "${forcedSourceTag}" — the "${seedRole}" role represents one shared identity, so this ` +
                    'tag isn’t editable. Pick a different role (or "custom (no template)") for a hand-typed tag.'
                  : 'Auto-stamps every activity + comment write from this token so the audit trail shows which ' +
                    'session did it. Leave blank to skip. Kebab-case recommended (letters, digits, dashes, ' +
                    'underscores; max 64 chars).'}
              </span>
            </label>

            {/* Expiry (optional) */}
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
              <span style={{ fontWeight: 500 }}>Expires (optional)</span>
              <input
                type="date" value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                style={{ padding: '6px 10px', fontSize: 13, border: '1px solid var(--border-strong)', borderRadius: 4, maxWidth: 220 }}
              />
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                Leave blank for 7-day expiry (default, CTSK-427). Set a date for a custom
                duration, or pick the <code>pats-operator</code> / <code>pats-admin</code> role
                (or the Admin tier) for no expiry.
              </span>
            </label>

            {/* Submit / cancel */}
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="submit" disabled={!canSubmit}
                style={{
                  padding: '6px 14px', fontSize: 13,
                  background: canSubmit ? 'var(--accent-primary)' : 'var(--border-subtle)',
                  color: '#fff', border: 'none', borderRadius: 4,
                  cursor: canSubmit ? 'pointer' : 'not-allowed', fontWeight: 600,
                }}
              >
                {mintMut.isPending ? 'Minting…' : 'Mint token'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setCreateOpen(false);
                  setName(''); setTier('update'); setCustomScopes(new Set()); setExpiresAt(''); setSourceTag(''); setOrgId(''); setSourceRole('');
                  setScopeSearch(''); setScopeSectionsOpen({});
                }}
                style={{
                  padding: '6px 14px', fontSize: 13,
                  background: 'transparent', color: 'var(--text-muted)',
                  border: '1px solid var(--border-subtle)', borderRadius: 4,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* ── Token list ────────────────────────────────────────── */}
        {patsQ.isLoading ? (
          <div style={{ color: 'var(--text-muted)' }}>Loading tokens…</div>
        ) : pats.length === 0 ? (
          <div style={{ color: 'var(--text-muted)', fontSize: 13, fontStyle: 'italic', padding: '12px 0' }}>
            No active tokens. Click <strong>New token</strong> above to mint one.
          </div>
        ) : (
          <>
            {/* CTSK-204 delta 2 — tab strip with counts, analogous to
                ExternalClientsNest's Pending/Approved/Denied/Revoked/All. */}
            <div style={{
              display: 'flex',
              gap: 6,
              borderBottom: '1px solid var(--border-subtle)',
              marginBottom: 12,
            }}>
              {TAB_ORDER.map((tabKey) => {
                const isActive = activeTab === tabKey;
                const color = TAB_COLORS[tabKey];
                return (
                  <button
                    key={tabKey}
                    type="button"
                    onClick={() => setActiveTab(tabKey)}
                    className="btn btn--ghost"
                    style={{
                      borderRadius: 0,
                      borderBottom: isActive ? `2px solid ${color}` : '2px solid transparent',
                      padding: '8px 12px',
                      fontWeight: isActive ? 600 : 400,
                      color: isActive ? color : 'var(--text-muted)',
                    }}
                  >
                    {TAB_LABELS[tabKey]}
                    <span style={{
                      marginLeft: 6,
                      fontSize: 11,
                      background: 'var(--surface-sunken)',
                      padding: '1px 6px',
                      borderRadius: 8,
                      color: 'var(--text-secondary)',
                      fontWeight: 500,
                    }}>
                      {tabCounts[tabKey]}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* CTSK-466 — All-tab toolbar: search + Kind + State
                dropdowns + archive toggle, right-aligned above the
                table (distinct from the tab strip on the left). */}
            {isAllTab && (
              <ToolbarRow
                search={search}
                onSearchChange={setSearch}
                searchPlaceholder="Search name, id, tag…"
                kindOptions={PAT_KIND_OPTIONS}
                kind={kindFilter}
                onKindChange={setKindFilter}
                stateOptions={PAT_STATE_OPTIONS}
                state={stateFilter}
                onStateChange={setStateFilter}
                showArchived={showArchived}
                onShowArchivedChange={setShowArchived}
                archivedHiddenCount={archivedCount}
              />
            )}

            <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 0 }}>
              {activeCount} active total · Tokens authenticate daemons and MCP servers over Bearer auth.
              {/* CTSK-466 — count-under-filter (CTSK-461 discipline):
                  whenever the All-tab toolbar/archive filters narrow the
                  view, say so explicitly instead of letting the table
                  silently disagree with the tab badge. */}
              {isAllTab && sortedRows.length !== tabCounts.all && (
                <>
                  {' '}· Showing {sortedRows.length} of {tabCounts.all}
                  {!showArchived && archivedCount > 0 && <> ({archivedCount} archived hidden)</>}
                </>
              )}
            </p>

            {sortedRows.length === 0 ? (
              <div style={{ padding: 24, color: 'var(--text-muted)', textAlign: 'center' }}>
                {filteredRows.length > 0 ? (
                  // CTSK-466 — rows exist in this tab but the toolbar /
                  // archive filters hid them all.
                  <>No tokens match the current filters.</>
                ) : (
                  <>No tokens in <strong>{TAB_LABELS[activeTab]}</strong>.</>
                )}
                {activeTab === 'revoked' && (
                  <div style={{ marginTop: 8, fontSize: 13 }}>
                    Tokens you revoke appear here with their revocation date (CTSK-319).
                  </div>
                )}
              </div>
            ) : (
              // CTSK-204 follow-up (2026-07-29): the initial ship kept the
              // full 9-column layout from CTSK-192 (Name | Tier | Kind |
              // PAT id | Prefix | Last used | Created | State | Actions)
              // with a `tableLayout: 'fixed'` colgroup, forcing every
              // column into a narrow slot and every row to double-height
              // via State's secondary-line pattern. Result: the MCP-ROLE
              // chip visually "stacked" (the row was tall enough to
              // look like each cell was on its own line), and Actions
              // wrapped onto multiple lines when both Open request +
              // Revoke were present. Fix: consolidate to 6 columns (Name
              // | Type | PAT id | Last used | State | Actions), drop the
              // fixed layout + colgroup so cells size to content, merge
              // Tier + Kind into a single "Type" cell rendered inline,
              // move secondary lines (org lock, expiry date) into title
              // tooltips, drop the Created + Prefix columns (redundant
              // with Last used + PAT id), and drop the reserved-slot
              // pattern in Actions so buttons flow naturally. Matches
              // ExternalClientsNest's 5-column grammar; both surfaces
              // now feel like the same table. */
              // CTSK-466 — header now renders via the shared RowTable
              // shell (SortableTh per column, ?sort=&dir= persisted);
              // same .grid table treatment as before.
              <RowTable columns={PAT_COLUMNS} sort={sort} onSortChange={onSortChange}>
                  {sortedRows.map(({ pat: p, isMcpRole, state, kind }) => {
                    // 0.1.54 — resolve mintOrgId to a friendly name so
                    // operators aren't reading raw ULIDs to tell which
                    // org a token is locked to. Now surfaces as an inline
                    // mono chip on the Name cell instead of a secondary
                    // line (2026-07-29 layout fix — the secondary line
                    // was doubling row height for every row and giving
                    // the whole table a stacked feel).
                    const lockedOrg = p.mintOrgId
                      ? orgOptions.find((o) => o.id === p.mintOrgId)
                      : null;
                    const lockedLabel = p.mintOrgId
                      ? (lockedOrg ? lockedOrg.name : p.mintOrgId)
                      : null;
                    const expiryLabel = p.expiresAt
                      ? `Expires ${new Date(p.expiresAt).toLocaleDateString()}`
                      : 'Never expires';
                    return (
                    <tr
                      key={p.id}
                      // CTSK-204 delta 6 — row-clickable. The PAT-detail
                      // view doesn't exist yet (separate ticket); this
                      // reserves the click surface now so adding that
                      // page later doesn't require touching every row's
                      // markup again. Every interactive element inside
                      // the row stops propagation so this doesn't hijack
                      // copy/open/revoke clicks.
                      onClick={() => toast.info(`Token detail view for "${p.name}" is coming in a follow-up ticket.`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td title={lockedLabel ? `${p.name} — locked to org: ${lockedLabel}` : p.name}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: 500 }}>{p.name}</span>
                          {lockedLabel && (
                            <span
                              title={`Locked to org: ${lockedLabel}${p.mintOrgId ? ` (${p.mintOrgId})` : ''}`}
                              style={{
                                fontSize: 10,
                                fontFamily: 'var(--font-mono)',
                                color: 'var(--text-muted)',
                                background: 'var(--surface-sunken)',
                                padding: '1px 6px',
                                borderRadius: 3,
                                border: '1px solid var(--border-subtle)',
                              }}
                            >
                              org: {lockedLabel}
                            </span>
                          )}
                        </span>
                      </td>
                      {/* CTSK-204 follow-up: merged Tier + Kind into a
                          single Type cell to eliminate the empty-space
                          look in narrow-column layouts. TierBadge and
                          KindIcon render inline; row stays single-line. */}
                      <td>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
                          <TierBadge tier={p.tier} isMcpRole={isMcpRole} />
                          {/* CTSK-466 — Kind chip-click filter (All tab
                              only — the toolbar the filter lives in is
                              only rendered there; chips stay inert on
                              the single-axis tabs). */}
                          {isAllTab ? (
                            <ChipFilterButton
                              active={kindFilter === kind}
                              title={kindFilter === kind ? 'Clear kind filter' : `Filter by kind: ${kind}`}
                              onToggle={() => setKindFilter(kindFilter === kind ? '' : kind)}
                            >
                              <KindIcon kind={kind} />
                            </ChipFilterButton>
                          ) : (
                            <KindIcon kind={kind} />
                          )}
                          {p.sourceRole && (
                            // CTSK-250 — "minted as role X" audit chip for
                            // dropdown-seeded tokens. Inline mono chip keeps
                            // the row single-line (CTSK-204 layout rule).
                            <span
                              title={`Minted as role: ${p.sourceRole} — scopes were seeded from this ROLE_CATALOG entry at mint time (CTSK-250)`}
                              style={{
                                fontSize: 10,
                                fontFamily: 'var(--font-mono)',
                                color: 'var(--text-muted)',
                                background: 'var(--surface-sunken)',
                                padding: '1px 6px',
                                borderRadius: 3,
                                border: '1px solid var(--border-subtle)',
                              }}
                            >
                              role: {p.sourceRole}
                            </span>
                          )}
                        </span>
                      </td>
                      {/* CTSK-192: PAT id — truncated with click-to-copy.
                          Full ULID surfaces via the title hover. Prefix
                          column was dropped in 2026-07-29 layout fix
                          (redundant with the id + reachable via detail
                          view when it lands). */}
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button
                          type="button"
                          title={`Copy full PAT id\n${p.id}\n\nPrefix: ${p.prefix}…`}
                          onClick={(e) => {
                            e.stopPropagation();
                            // CTSK-282: wrapper works on insecure origins too.
                            void copyToClipboard(p.id).then((ok) => {
                              if (ok) toast.success(`Copied PAT id ${p.id.slice(0, 8)}…`);
                              else toast.error('Copy failed — clipboard unavailable');
                            });
                          }}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            padding: '2px 6px', fontSize: 11,
                            fontFamily: 'var(--font-mono)',
                            border: 'none', borderRadius: 3,
                            background: 'transparent', color: 'inherit',
                            cursor: 'pointer',
                          }}
                        >
                          {p.id.slice(0, 8)}… <Copy size={10} />
                        </button>
                      </td>
                      {/* CTSK-204 delta 5 — relative-time last-used,
                          absolute date + created-at on hover-title. The
                          separate Created column was dropped in the
                          2026-07-29 layout fix (redundant — created-at
                          rides as a tooltip line here instead). */}
                      <td
                        style={{ color: 'var(--text-muted)', fontSize: 12, whiteSpace: 'nowrap' }}
                        title={
                          (p.lastUsedAt ? `Last used ${new Date(p.lastUsedAt).toLocaleString()}` : 'Never used') +
                          `\nCreated ${new Date(p.createdAt).toLocaleString()}`
                        }
                      >
                        {p.lastUsedAt ? formatRelativeTime(p.lastUsedAt) : '—'}
                      </td>
                      {/* CTSK-204 delta 3 — State column (replaces Expires).
                          Layout fix (2026-07-29): expiry date moved from a
                          secondary line under the badge into the title
                          tooltip. Kept the badge on its own single line so
                          rows have a consistent height and MCP-ROLE +
                          state chips read at a glance. */}
                      {/* CTSK-319: revoked rows show the revocation
                          timestamp on hover instead of the expiry line. */}
                      <td
                        style={{ fontSize: 12, whiteSpace: 'nowrap' }}
                        title={
                          state === 'revoked' && p.revokedAt
                            ? `Revoked ${new Date(p.revokedAt).toLocaleString()}`
                            : expiryLabel
                        }
                      >
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          {/* CTSK-466 — State chip-click filter (All tab
                              only, same rationale as the Kind chip). */}
                          {isAllTab ? (
                            <ChipFilterButton
                              active={stateFilter === state}
                              title={stateFilter === state ? 'Clear state filter' : `Filter by state: ${state}`}
                              onToggle={() => setStateFilter(stateFilter === state ? '' : state)}
                            >
                              <StateBadge state={state} />
                            </ChipFilterButton>
                          ) : (
                            <StateBadge state={state} />
                          )}
                          {isCustomPatReviewOverdue(p) && (
                            // CTSK-456 — quarterly custom-tier PAT review
                            // runbook (docs/runbooks/custom-pat-review.md,
                            // CTSK-431 §6 Decision 4). Informational only —
                            // does NOT block or alter any action on the
                            // token (audit-flag-only enforcement stance,
                            // runbook §5). Orthogonal axis to State
                            // (lifecycle) — a token can be simultaneously
                            // "active" and "review overdue".
                            <span
                              title={`Review overdue since ${new Date(p.reviewDueAt as string).toLocaleString()} — see docs/runbooks/custom-pat-review.md`}
                              style={{
                                display: 'inline-block',
                                padding: '2px 8px',
                                borderRadius: 10,
                                background: '#EF444420',
                                color: '#EF4444',
                                fontSize: 11,
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: 0.4,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              REVIEW OVERDUE
                            </span>
                          )}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {/* Layout fix (2026-07-29): dropped the fixed
                            118px reserved-slot for Open request. Buttons
                            now flow naturally in an inline-flex —
                            single-line on any reasonable column width.
                            Matches ExternalClientsNest's Change-role +
                            Revoke pattern. */}
                        <div
                          style={{ display: 'inline-flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {isMcpRole && (
                            <a
                              href={`/documents/${p.accessRequestDocId}`}
                              title={`Open source access-request doc\n${p.accessRequestDocId}`}
                              style={{
                                ...actionButtonBaseStyle,
                                border: '1px solid var(--accent-primary)',
                                background: 'transparent', color: 'var(--accent-primary)',
                                textDecoration: 'none',
                              }}
                            >
                              <ExternalLink size={12} /> Open request
                            </a>
                          )}
                          {/* CTSK-319: no Revoke action on already-revoked
                              rows (DELETE would 404 — revocation is
                              terminal; the row is audit surface only). */}
                          {state !== 'revoked' && (
                            <button
                              type="button"
                              title="Revoke this token — subsequent Bearer requests will 401"
                              onClick={() => {
                                if (confirm(`Revoke "${p.name}"? Bearer requests with this token will 401 immediately.`)) {
                                  revokeMut.mutate(p.id);
                                }
                              }}
                              disabled={revokeMut.isPending}
                              style={{
                                ...actionButtonBaseStyle,
                                border: '1px solid var(--danger-primary, #B3261E)',
                                background: 'transparent', color: 'var(--danger-primary, #B3261E)',
                              }}
                            >
                              <Trash2 size={12} /> Revoke
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                  })}
              </RowTable>
            )}
          </>
        )}
      </section>

      {/* ── Footer notes ────────────────────────────────────────── */}
      {/* CTSK-204: wrapped in CollapsiblePanel per the ticket's preserve
          note ("keep as expandable") — reuses the existing left-stack
          collapse primitive rather than hand-rolling one. */}
      <CollapsiblePanel storageKey="pats:tier-semantics" title="Tier semantics (0.1.39+)">
        <ul style={{ margin: '6px 0 0 16px', padding: 0, lineHeight: 1.6, fontSize: 12, color: 'var(--text-muted)' }}>
          <li>
            <TierBadge tier="read-only" /> — observability sessions, dashboard builders, audit runs.
            Deliberately excludes <code>workitems:view</code> — the read-only tier can list but not fetch full detail.
          </li>
          <li>
            <TierBadge tier="update" /> — general-purpose automation. Reads + writes on work items and comments;
            transitions including review-gated. <strong>Recommended default</strong> for MCP and daemon PATs.
          </li>
          <li>
            <TierBadge tier="create" /> — automation that also mints new tickets and projects. Superset of <code>update</code>.
          </li>
          <li>
            <TierBadge tier="admin" /> — orchestrators, script daemons. Includes <code>pats:manage</code> and <code>system:admin</code>.
            Only effective if the caller already holds those caps (intersection guard).
          </li>
          <li>
            <TierBadge tier="custom" /> — hand-picked scopes. Frozen contract; does not auto-heal on future cap additions.
            Discouraged for shared integrations.
          </li>
          <li>
            {/* CTSK-192 (2026-07-22): MCP-ROLE is a display flag layered
                ON TOP of tier=custom, not a distinct tier. Rendered when
                a PAT was minted via the External Clients Nest approve-
                and-mint or change-client-role flow — visible via the
                access_request_doc_id back-reference on the row. */}
            <TierBadge tier="custom" isMcpRole /> — a custom-tier PAT minted through the External Clients Nest
            approve-and-mint or change-client-role flow. Not a distinct tier — a display flag layered on
            <code>custom</code>. The <em>Open request</em> action navigates to the source access-request
            doc. Introduced 0.1.57 (CTSK-192).
          </li>
        </ul>
        <p style={{ margin: '10px 0 0 0', fontSize: 12, color: 'var(--text-muted)' }}>
          {/* CTSK-194 (2026-07-22): document the new pats:view_all cap +
              pats-operator role that gate the External Clients Nest
              admin view. Operators reading this table need to know the
              handle exists when they mint a read-only PAT for someone
              who should see access-request docs but not approve them. */}
          <strong>Roles &amp; caps (0.1.58+ · CTSK-194):</strong>{' '}
          The new <code>pats:view_all</code> capability grants org-wide visibility into every
          access-request doc in a nest, regardless of the caller's sourceTag. It's held by
          cookie-auth <em>owner</em> + <em>operator</em> role members by default, and can be minted
          on a PAT via the new <code>pats-operator</code> role handle in <code>ROLE_CATALOG</code>
          (read-only visibility; does NOT grant approve/deny/change-role/revoke).
        </p>
        <p style={{ margin: '10px 0 0 0', fontSize: 12, color: 'var(--text-muted)' }}>
          Effective scope on each request is <code>userCurrentCaps ∩ (storedSnapshot ∪ liveTierExpand(tier))</code>.
          See <code>docs/pats/api.md</code> §3.1 for the full formula.
        </p>
      </CollapsiblePanel>
    </div>
  );
}

/** Compact colored badge for a tier. Rendered next to the token name
 *  in the list + in the reveal banner + in the tier picker.
 *  CTSK-192 (2026-07-22): when `isMcpRole` is true (row has an
 *  access_request_doc_id back-reference), render "MCP-ROLE" instead
 *  of the raw "CUSTOM" tier label so operators can distinguish
 *  MCP-minted session PATs from hand-crafted custom-tier PATs at a
 *  glance. Uses a distinct accent color for the same reason. */
function TierBadge({ tier, isMcpRole = false }: { tier: PatTierName; isMcpRole?: boolean }) {
  const label = isMcpRole ? 'MCP-ROLE' : tier;
  const color = isMcpRole ? '#7c3aed' /* purple/violet */ : tierBadgeColor(tier);
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center',
        padding: '1px 8px', fontSize: 11, fontWeight: 600,
        letterSpacing: 0.3, textTransform: 'uppercase',
        color: '#fff', background: color,
        borderRadius: 10,
      }}
    >
      {label}
    </span>
  );
}
